import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Text } from '@nula/core-ui';
import styles from './error-message.module.css';

const root = cva(styles.root);

interface ErrorMessageType {
  type: 'error';
  id: string;
  timestamp: number;
  code: string;
  userMessage: string;
  retryPossible?: boolean;
  isFirstInTurn?: boolean;
}

interface ErrorMessageProps {
  message: ErrorMessageType;
  /** Optional trailing affordance. Omitted for generic errors. */
  action?: ReactNode;
  className?: string;
}

export function ErrorMessage({ message, action, className }: ErrorMessageProps) {
  // Recoverable errors (provider exhaustion, transient 5xx) show a
  // discreet retry hint. The hint is intentionally not a button — the
  // existing chat input is the retry surface. Fatal errors omit the hint
  // so we don't promise a retry that won't help.
  const showRetryHint = message.retryPossible === true;
  return (
    <Flex align="center" gap="xs" className={clsx(root(), className)} role="alert">
      <Text className={styles.icon}>⚠</Text>
      <Flex vertical gap="xs" className={styles.body}>
        <Text className={styles.message}>{message.userMessage}</Text>
        {showRetryHint && (
          <Text className={styles.retryHint} data-testid="error-retry-hint">
            You can try sending your message again.
          </Text>
        )}
        {action}
      </Flex>
    </Flex>
  );
}
